import { type FC } from 'react';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { BoundaryPreviewComponent } from '~/boundary/settings/lineExample/boundaryPreviewComponent';
import {
  type MapBoundaryLabelLegendCallback,
  useMapBoundaryLabelLegend,
} from '~/map/map/boundary/boundaryView/mapBoundaryPolygons/useMapBoundaryLabelLegend';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryLabelValueConfig,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

type Props = Readonly<{
  className?: string;
  squareSize?: number;
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
}>;

const getLabelLegendPreviewText = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  getMapBoundaryLabelLegend: MapBoundaryLabelLegendCallback,
): string => {
  const legend = getMapBoundaryLabelLegend(boundaryTerritoryGroup);
  if (!legend) {
    return 'Column name:';
  }
  return `${legend}: `;
};

export const BoundarySettingsBoundaryPreviewComponent: FC<Props> = ({
  className,
  squareSize,
  boundaryTerritoryGroup,
}) => {
  const { getMapBoundaryLabelLegend } = useMapBoundaryLabelLegend();
  const { settings } = boundaryTerritoryGroup;
  const labelIncludesName = !settings.hideLabels;
  const currentLabelValueSettings: BoundaryTerritoryLabelValueConfig = settings.labelValueConfig;
  const boundaryTerritoryType = settings.labelValueConfig.isValueCustom ?
    settings.labelValueConfig.labelValueType ?? settings.boundaryTerritoryType :
    settings.boundaryTerritoryType;
  const labelIncludesLegend = currentLabelValueSettings.includeLegend;
  const labelIncludesValue = currentLabelValueSettings.showValue && boundaryTerritoryType !== BoundaryTerritoryType.Manual;

  const labelLegendPreviewText = getLabelLegendPreviewText(boundaryTerritoryGroup, getMapBoundaryLabelLegend);
  const labelValuePreviewText = boundaryTerritoryType === BoundaryTerritoryType.Groups ? 'Group Name' : '42';

  const showLabel = labelIncludesName || labelIncludesValue;
  const labelPreviewText = (
    <>
      {labelIncludesName ? 'Boundary name' : ''}
      {labelIncludesName && labelIncludesValue ? <br /> : null}
      {labelIncludesValue && labelIncludesLegend ? labelLegendPreviewText : ''}
      {labelIncludesValue ? labelValuePreviewText : ''}
    </>
  );

  const lineWidth = settings.style.lineWidth;
  const lineColor = createColor(settings.style.lineColor);

  const labelTextColor = settings.labelStyle.fontColor;
  const labelBackgroundColor = settings.labelStyle.backgroundColor;

  return (
    <BoundaryPreviewComponent
      className={className}
      squareSize={squareSize}
      lineColor={lineColor}
      lineWidth={lineWidth}
      showLabel={showLabel}
      labelBackgroundColor={labelBackgroundColor}
      labelTextColor={labelTextColor}
      labelPreviewText={labelPreviewText}
    />
  );
};
